// Subscription tier configuration using Stripe lookup_keys

import { isProduction } from '@/lib/environment';

export type SubscriptionTier = 'free' | 'starter' | 'pro' | 'unlimited';
export type BillingCycle = 'monthly' | 'annual';

export interface TierConfig {
  name: string;
  projectLimit: number;
  monthlyPrice: number;    // dollars, monthly billing
  annualPrice: number;     // dollars, total per year
  annualMonthly: number;   // dollars, annual price / 12 (display)
  features: string[];
}

const APP_TAG = 'greenlight';

export const SUBSCRIPTION_TIERS: Record<SubscriptionTier, TierConfig> = {
  free: {
    name: 'Free',
    projectLimit: 2,
    monthlyPrice: 0,
    annualPrice: 0,
    annualMonthly: 0,
    features: [
      '2 active projects',
      'Unlimited reviewers',
      'Email forwarding',
      'Basic comments',
    ],
  },
  starter: {
    name: 'Starter',
    projectLimit: 5,
    monthlyPrice: 19,
    annualPrice: 180,
    annualMonthly: 15,
    features: [
      '5 active projects',
      'Unlimited reviewers',
      'Email forwarding',
      'Comments & annotations',
      'Version history',
    ],
  },
  pro: {
    name: 'Pro',
    projectLimit: 12,
    monthlyPrice: 35,
    annualPrice: 336,
    annualMonthly: 28,
    features: [
      '12 active projects',
      'Unlimited reviewers',
      'Email forwarding',
      'Comments & annotations',
      'Version history',
      'Priority support',
    ],
  },
  unlimited: {
    name: 'Unlimited',
    // 999 = soft cap to match DB-level abuse ceiling. Marketing copy still says "Unlimited".
    projectLimit: 999,
    monthlyPrice: 89,
    annualPrice: 840,
    annualMonthly: 70,
    features: [
      'Unlimited projects',
      'Unlimited reviewers',
      'Email forwarding',
      'Comments & annotations',
      'Version history',
      'Priority support',
      'Custom branding (coming soon)',
    ],
  },
};

// Product ID mappings for tier resolution from check-subscription
const PRODUCT_IDS: Record<string, SubscriptionTier> = {
  // Test products (from create-stripe-prices script)
  'prod_UR9m9yUcGhbDfu': 'starter',
  'prod_UR9mdpe6mki1xU': 'pro',
  'prod_UR9m9OKlGQeA9z': 'unlimited',
  // Live products
  'prod_UR9yT84P32SKVS': 'starter',
  'prod_UR9y9bPWjwrg7d': 'pro',
  'prod_UR9yaZ73yrGPJT': 'unlimited',
  // Legacy test products (old hardcoded)
  'prod_TibpavhjYThzDx': 'starter',
  'prod_TibqBVbPefNGLN': 'pro',
  'prod_TibqtnNKbLJX7F': 'unlimited',
  // Legacy live products
  'prod_TiI81j4KX631kp': 'starter',
  'prod_TiI8AgEzS9yPCy': 'pro',
  'prod_TiI8hA2vYgYRqj': 'unlimited',
};

/**
 * Build a Stripe lookup_key for the given tier and billing cycle.
 * The env suffix (_test / _live) is appended based on the current domain.
 */
export const getLookupKey = (tier: SubscriptionTier, billing: BillingCycle): string => {
  const env = isProduction() ? 'live' : 'test';
  return `${APP_TAG}_${tier}_${billing}_${env}`;
};

/** @deprecated — use getLookupKey instead. Kept for backward compat during transition. */
export const getPriceId = (_tier: SubscriptionTier): string | null => {
  // No longer returns hardcoded IDs; callers should migrate to getLookupKey
  return null;
};

export const getTierFromProductId = (productId: string | null): SubscriptionTier => {
  if (!productId) return 'free';
  return PRODUCT_IDS[productId] ?? 'free';
};

export const getProjectLimit = (tier: SubscriptionTier): number => {
  return SUBSCRIPTION_TIERS[tier].projectLimit;
};

export const canCreateProject = (activeProjectCount: number, tier: SubscriptionTier): boolean => {
  const limit = getProjectLimit(tier);
  return activeProjectCount < limit;
};

export const getTierConfig = (tier: SubscriptionTier): TierConfig => {
  return SUBSCRIPTION_TIERS[tier];
};

export const getAllTiers = (): SubscriptionTier[] => {
  return ['free', 'starter', 'pro', 'unlimited'];
};

/**
 * Calculate savings percentage for annual vs monthly billing.
 */
export const getAnnualSavings = (tier: SubscriptionTier): number => {
  const config = SUBSCRIPTION_TIERS[tier];
  if (config.monthlyPrice === 0) return 0;
  const monthlyTotal = config.monthlyPrice * 12;
  return Math.round((1 - config.annualPrice / monthlyTotal) * 100);
};
